import React, { FC, useCallback, useState } from 'react';
import { connect } from 'react-redux';

import cn from 'classnames';
import { Button, LoaderSpinner, TextField } from 'components/UtilElements';
import { AuthAction, AuthActions, LinkEmailResponse } from 'containers/AuthStateManager';
import { AuthScreens } from 'globals/constants';
import { API, request } from 'redux/api';
import { RootState } from 'redux/reducers';
import isEmail from 'validator/lib/isEmail';

const ltiLinkEmail = (emailId: string) => {
  return request<LinkEmailResponse>({
    method: 'POST',
    url: API.LTI_LINK_EMAIL,
    data: { emailId },
  });
};

interface GetEmailProps {
  accessData: SessionData | null;
  setAuthState(action: AuthAction): void;
}

const GetEmail: FC<GetEmailProps> = ({ accessData, setAuthState }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!isEmail(email)) {
        return setError('Please enter a valid email address');
      } else if (email === accessData?.emailId) {
        return setError('Please use a different email address');
      }

      try {
        setError('');
        setIsLoading(true);
        const response = await ltiLinkEmail(email);
        setAuthState({
          type: AuthActions.GOTO_LINK_ACADLY_PASSWORD,
          payload: response,
        });
      } catch (e) {
        console.error(e);
        setError(e.response ? e.response.data.message : e.message);
        setIsLoading(false);
      }
    },
    [accessData, email, setAuthState]
  );

  if (!accessData) return null;

  return (
    <>
      <h2 className='auth__title'>Link an existing Acadly account</h2>
      <form className='auth__panel' onSubmit={onSubmit}>
        <p className='auth__message'>
          If you have used Acadly for one of your courses at{' '}
          <strong>{accessData.organizationDetails.name}</strong>, please enter your login credentials
        </p>
        <TextField
          hasError={!!error}
          selector='auth__input'
          wrapperClassName='auth__input-wrapper'
          placeholder='Your registered email address'
          onchange={(e) => setEmail(e.target.value)}
          value={email}
          type='email'
        />
        <div className={cn('auth__error', { shake: !!error })}>{error}</div>
        <div
          className={cn(
            'auth__action-container',
            'auth__action-container--right',
            'auth__action-container--horizontal'
          )}
        >
          <Button formButton type='ghost' selector={cn('btn-primary', 'auth__action', 'auth__action--sm')}>
            {isLoading ? <LoaderSpinner /> : 'Next'}
          </Button>
        </div>
        <p className='auth__note'>
          Planning to use Acadly for the first time?{' '}
          <button
            className='auth__link'
            onClick={() => {
              setAuthState({
                type: AuthActions.SET_SCREEN_WITH_CONTEXT,
                payload: { context: 'email', screen: AuthScreens.VERIFY_CODE },
              });
            }}
          >
            Create a New Account
          </button>
        </p>
      </form>
    </>
  );
};

const mapStateToProps = ({ getAccess }: RootState) => {
  return { accessData: getAccess };
};

export default connect(mapStateToProps)(GetEmail);
